import type { BenefitDetailDto, BenefitGrantDto, BenefitWorkflowResponse } from "./benefit-types"
import { getBenefitProgramLabel, grantStatusLabels } from "./benefit-enums"
import { enumLabel } from "./benefit-format"
import { usableGrantAuthority } from "./grant-workspace"

const reviewLabels: Record<string, string> = { Created: "Awaiting review", Proofed: "Proofed", FixRequested: "Corrections requested", Certified: "Certified", Invalidated: "Invalidated" }
export interface AssistanceRow {
  key: string
  name: string
  request: "Requested" | "No saved request" | "Request unavailable"
  grant: BenefitGrantDto | null
  review: string
}

/** Application requests are not year-specific. Never infer them from a grant or calculation.
 * Retain every grant/year/revision; ambiguous grant or review identities have no review readback.
 */
export function overviewAssistance(benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null): AssistanceRow[] {
  const requested = benefit.requestedBenefits ?? []
  const grants = benefit.benefitGrants ?? []
  const identity = (program: number) => ({
    name: requested.find(item => item.programId === program)?.name.trim() || getBenefitProgramLabel(program),
    request: benefit.requestedBenefitsReadUnavailable ? "Request unavailable" as const : requested.some(item => item.programId === program) ? "Requested" as const : "No saved request" as const,
  })
  const rows: AssistanceRow[] = grants.map((grant, index) => {
    const { program, year, revision } = grant.benefitProgramInstance
    let review = "Review unavailable"
    if (workflow?.authority === "Legacy" && workflow.display?.authority === "Legacy" && workflow.display.readUnavailable === false) {
      review = `Legacy: ${enumLabel(grantStatusLabels, grant.grantStatus)}`
    } else if (usableGrantAuthority(workflow)) {
      const matches = workflow.grantReviews.filter(item => item.programId === program && item.programYear === year)
      const grantMatches = grants.filter(item => item.benefitProgramInstance.program === program && item.benefitProgramInstance.year === year)
      if (matches.length === 1 && grantMatches.length === 1) review = reviewLabels[matches[0]!.state] ?? `Unknown (${matches[0]!.state})`
    }
    return { key: `grant/${program}/${year}/${revision}/${index}`, ...identity(program), grant, review }
  })
  for (const program of new Set(requested.map(item => item.programId))) {
    if (!grants.some(grant => grant.benefitProgramInstance.program === program)) rows.push({ key: `request/${program}`, ...identity(program), grant: null, review: "No recorded grant" })
  }
  return rows
}

export function overviewReviewSummary(rows: readonly AssistanceRow[]) {
  const counts = new Map<string, number>()
  for (const row of rows) if (row.grant) counts.set(row.review, (counts.get(row.review) ?? 0) + 1)
  return counts.size ? [...counts].map(([label, count]) => `${count} ${label.toLowerCase()}`).join(" · ") : "No recorded grants"
}
